<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'

import { storeToRefs } from 'pinia'

import PrevStepBtn from '@/components/frontend/profile/common/PrevStepBtn.vue'

import * as aggr from '@/api-generator/aggr'
import { feUserProfileAPI } from '@/APIs'
import { ProfileSteps } from '@/constant/user'
import appStore from '@/stores'

const { updateCurrentStep, setCurrentCorrelationId } = appStore.useFEUserProfileStore
const { getUserInfo } = appStore.useAuthStore
const { currentCorrelationId, newUserEmail } = storeToRefs(appStore.useFEUserProfileStore)
const { userInfo } = storeToRefs(appStore.useAuthStore)

const prevStep = ref<ProfileSteps>(ProfileSteps.UPDATE_EMAIL_STEP_1)

const remainingSeconds = ref(30)

const startCountdown = () => {
  const interval = setInterval(() => {
    if (remainingSeconds.value > 0) {
      remainingSeconds.value--
    } else {
      clearInterval(interval)
    }
  }, 1000)
}

const code = ref(Array(6).fill(''))
const inputRefs = ref<HTMLInputElement[]>([])

const moveNext = (index: number) => {
  if (index < code.value.length - 1 && code.value[index] !== '') {
    inputRefs.value[index + 1].focus()
  }
}

const movePrev = (event: KeyboardEvent, index: number) => {
  if (event.key === 'Backspace') {
    if (code.value[index] === '' && index > 0) {
      inputRefs.value[index - 1].focus()
    }
    code.value[index] = ''
  }
}

const isCodeComplete = computed(() => code.value.every((digit) => digit !== ''))

const handleResendVerificationCode = async () => {
  remainingSeconds.value = 30
  startCountdown()
  const reqBody: aggr.FEUserProfileResendVerificationCodeRq = {
    correlationId: currentCorrelationId.value,
    newEmail: newUserEmail.value
  }
  try {
    const { data } = await feUserProfileAPI.resendVerificationCode(reqBody)
    setCurrentCorrelationId(data.correlationId)
    verifyStatus.value = 'resend'
  } catch (error: any) {
    const errorMsg = error.response.data.message ? error.response.data.message : error.message
    console.error(errorMsg)
  }
}

const verifyStatus = ref('')

const verifyCodeMsg = computed(() => {
  switch (verifyStatus.value) {
    case 'resend':
      return '已重新傳送'
    case 'error':
      return '驗證失敗！請輸入正確的驗證碼'
    default:
      return ''
  }
})

const msgClass = computed(() => {
  return {
    'verify-error-msg': verifyStatus.value === 'error',
    'verify-resend-msg': verifyStatus.value === 'resend'
  }
})

const isLoading = ref(false)

const handleVerifyEmailUpdate = async () => {
  if (!isCodeComplete.value) {
    return
  }
  isLoading.value = true
  const reqData: aggr.FEUserProfileVerifyEmailChangeRq = {
    correlationId: currentCorrelationId.value,
    newEmail: newUserEmail.value,
    verificationCode: code.value.join('')
  }
  try {
    const { data } = await feUserProfileAPI.verifyEmailUpdate(reqData)
    if (!data.status) {
      verifyStatus.value = 'error'
      return
    }
    updateCurrentStep(ProfileSteps.UPDATE_EMAIL_DONE)
    getUserInfo()
  } catch (error: any) {
    console.error(error)
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  startCountdown()
  if (inputRefs.value.length > 0) {
    inputRefs.value[0].focus()
  }
})
</script>

<template>
  <div class="profile-edit-email-step2">
    <PrevStepBtn :prev-step="prevStep" />
    <div class="mb-5">
      <h3 class="profile-step-title mb-3">變更驗證信箱 (2/2)</h3>
      <div class="text-xs leading-[18px] text-app-black-900">
        <p class="mb-3 font-medium">驗證碼已發送至 {{ newUserEmail }}，請於下方輸入，以確認您的變更。</p>
        <p class="mb-3">當前信箱：{{ userInfo?.email }}</p>
        <div class="flex items-center gap-1">
          <p>新信箱：{{ newUserEmail }}</p>
          <button
            type="button"
            class="text-xs text-app-brand-blue-500"
            @click="updateCurrentStep(ProfileSteps.UPDATE_EMAIL_STEP_1)"
          >
            變更
          </button>
        </div>
      </div>
    </div>
    <form @keypress.enter.prevent="handleVerifyEmailUpdate">
      <div class="profile-edit-email-step2-verification">
        <p class="profile-edit-email-step2-verification-text text-app-black-400">
          請輸入您收到的信箱驗證碼
          <span v-if="remainingSeconds > 0" class="text-xs text-app-brand-blue-500"
            >(於{{ remainingSeconds }}秒後可重新發送)</span
          >
          <span v-else class="cursor-pointer text-xs text-app-brand-blue-500" @click="handleResendVerificationCode"
            >重新發送</span
          >
        </p>
        <div class="profile-edit-email-step2-verification-input-field">
          <input
            v-for="(digit, index) in code"
            :key="index"
            ref="inputRefs"
            v-model="code[index]"
            type="text"
            inputmode="numeric"
            maxlength="1"
            @input="moveNext(index)"
            @keydown="movePrev($event, index)"
          />
        </div>
        <div class="profile-edit-email-step2-verification-msg-wrapper">
          <p :class="msgClass">
            {{ verifyCodeMsg }}
          </p>
        </div>
      </div>
      <div class="profile-steps-btn-wrapper">
        <BaseButton @click="updateCurrentStep(ProfileSteps.PROFILE_INFO)">取消</BaseButton>
        <BaseButton
          class="w-[120px] border-app-brand-blue-500 bg-app-brand-blue-500 text-white"
          :disabled="!isCodeComplete"
          :loading="isLoading"
          @click="handleVerifyEmailUpdate"
        >
          <span v-if="isLoading">儲存中</span>
          <span v-else>儲存變更</span>
        </BaseButton>
      </div>
    </form>
  </div>
</template>
<style lang="scss" scoped>
.profile-edit-email-step2 {
  &-confirm {
    color: #000;
    font-size: 12px;
    letter-spacing: 1.147px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
    margin-top: 12px;
  }
  &-verification {
    &-text {
      font-size: 12px;
      letter-spacing: 1.147px;
      margin-bottom: 17px;
    }
    &-count-down {
      color: var(--primary-3);
    }
    &-input-field {
      display: flex;
      align-items: center;
      gap: 10px;
      max-width: 307px;
      input {
        width: 41px;
        height: 46px;
        border-radius: 4px;
        border: 1px solid var(--gray-d9);
        background-color: var(--bg-standard);
        outline: none;
        text-align: center;
        font-weight: 500;
        font-size: 16px;
      }
    }
    &-msg-wrapper {
      height: 14px;
      padding-top: 12px;
      font-size: 12px;
    }
  }
}

.verify-resend-msg {
  color: rgb(8, 111, 8);
  margin: 0;
}
.verify-error-msg {
  color: var(--danger);
  margin: 0;
}
</style>
